import React, { useState, useEffect } from 'react';
import { authService } from '../lib/auth';
import { User, UserRole } from '../types';

interface UserManagementProps {
  currentUser: User;
  onClose: () => void;
}

const UserManagement: React.FC<UserManagementProps> = ({ currentUser, onClose }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [newEmail, setNewEmail] = useState('');
  const [newRole, setNewRole] = useState<UserRole>('administrator');
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    setLoading(true);
    try {
      const fetchedUsers = await authService.getUsers();
      setUsers(fetchedUsers);
    } catch (e: any) {
      console.error("Failed to load users", e);
      setError("Nepodařilo se načíst seznam uživatelů.");
    } finally {
      setLoading(false);
    }
  };

  const handleAddUserRole = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    
    if (!newEmail.trim()) return;

    try {
      await authService.addUserRole(newEmail, newRole);
      setSuccess(`Role pro uživatele ${newEmail} byla nastavena.`);
      setNewEmail('');
      loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveUserRole = async (email: string) => {
    if (!window.confirm(`Opravdu chcete odebrat roli uživateli ${email}?`)) return;
    try {
      await authService.removeUserRole(email);
      loadUsers();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (currentUser.role !== 'superadministrator') {
    return <div className="text-red-600 p-4">Nemáte oprávnění prohlížet tuto sekci.</div>;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" role="dialog">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden max-h-[90vh] flex flex-col">
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-bold text-gray-800">Správa rolí administrátorů</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 overflow-y-auto">
          <div className="mb-4 p-3 bg-blue-50 text-blue-800 rounded border border-blue-200 text-xs">
              <strong>Důležité:</strong> Tato sekce spravuje pouze <em>oprávnění</em> v aplikaci. 
              Samotný uživatelský účet (e-mail a heslo) musí být vytvořen ve <a href="https://console.firebase.google.com/" target="_blank" rel="noopener noreferrer" className="underline font-bold">Firebase Console</a> v sekci Authentication.
          </div>

          {error && <div className="mb-4 p-3 bg-red-100 text-red-700 rounded border border-red-200">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-100 text-green-700 rounded border border-green-200">{success}</div>}

          {/* Add User Role Form */}
          <div className="mb-8 bg-gray-50 p-4 rounded-lg border border-gray-200">
            <h3 className="text-sm font-bold text-gray-700 uppercase mb-3">Přidat/Upravit roli uživatele</h3>
            <form onSubmit={handleAddUserRole} className="grid grid-cols-1 sm:grid-cols-12 gap-3 items-end">
              <div className="sm:col-span-7">
                <label className="block text-xs text-gray-500 mb-1">E-mail (musí se shodovat s Firebase Auth)</label>
                <input 
                    type="email" 
                    required 
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="w-full px-3 py-2 border rounded text-sm" 
                    placeholder="email@example.com"
                />
              </div>
              <div className="sm:col-span-3">
                <label className="block text-xs text-gray-500 mb-1">Role</label>
                <select 
                    value={newRole}
                    onChange={(e) => setNewRole(e.target.value as UserRole)}
                    className="w-full px-3 py-2 border rounded text-sm"
                >
                    <option value="administrator">Administrátor</option>
                    <option value="superadministrator">Superadmin</option>
                    <option value="user">Uživatel</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <button type="submit" className="w-full py-2 bg-blue-600 text-white rounded text-sm font-bold hover:bg-blue-700">
                    Uložit
                </button>
              </div>
            </form>
          </div>

          {/* User List */}
          <h3 className="text-sm font-bold text-gray-700 uppercase mb-3">Seznam definovaných rolí</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">E-mail</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Akce</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                    <tr>
                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">Načítám data...</td>
                    </tr>
                ) : (
                    users.map((user) => (
                      <tr key={user.uid}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{user.email}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${user.role === 'superadministrator' ? 'bg-purple-100 text-purple-800' : (user.role === 'administrator' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800')}`}>
                                {user.role}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {user.email !== currentUser.email && (
                              <button onClick={() => handleRemoveUserRole(user.email)} className="text-red-600 hover:text-red-900">Odebrat roli</button>
                          )}
                        </td>
                      </tr>
                    ))
                )}
                {!loading && users.length === 0 && (
                    <tr>
                        <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">Žádné role nejsou definovány.</td>
                    </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;